import { Stack, Tooltip, Typography, useTheme, Zoom } from "@mui/material";
import { useTranslation } from "react-i18next";
import Icon from "../../Icon/Icon";
import useLocalizedLabel from "../../../hooks/useLocalizedLabel";
import { parseDateToLgComun4 } from "../../../helpers/dateTransform";

/**
 * @returns Classification lines shown under the folio in the report header
 * @param {reportDetail} object report detail as returned by /report/detail/
 *
 * - Clasificación denunciante: RC-100 catalogue (`classification`)
 * - Clasificación EthicsGlobal: `eg_classification`
 * - Clasificación MercadoLibre: `meli_classification`, conditional
 *
 * El renglón de MercadoLibre se pinta como evento: el backend solo lo manda
 * cuando el tenant es de MercadoLibre y existe un ActivityLog de reclasificación
 * sobre el campo, así que aquí basta con preguntar si viene.
 */

const ClassificationLine = ({ label, value, iconName, color }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
    {iconName && <Icon name={iconName} color={color} size={13} />}
    <Typography component="span" fontSize={11.5} fontWeight={600} color={color}>
      {label}:
    </Typography>
    <Typography component="span" fontSize={11.5}>
      {value}
    </Typography>
  </Stack>
);

export const ClassificationLines = ({ reportDetail }) => {
  const theme = useTheme();
  const [t] = useTranslation("report");
  const getLocalizedLabel = useLocalizedLabel();

  // El catálogo entrega la ruta completa; la hoja por sí sola es ambigua
  // ("Género" no dice nada sin "Discriminación").
  const toBreadcrumb = (node) => {
    if (!node) return "";
    const path = Array.isArray(node.path) && node.path.length ? node.path : [node];
    return path.map(getLocalizedLabel).filter(Boolean).join(" › ");
  };

  const complainant = getLocalizedLabel(reportDetail?.classification);
  const ethicsGlobal = toBreadcrumb(reportDetail?.eg_classification);
  const mercadoLibre = toBreadcrumb(reportDetail?.meli_classification);

  const reclassifiedAt = reportDetail?.meli_classification?.changed_at;
  const reclassifiedTimes = reportDetail?.meli_classification?.times || 0;

  return (
    <Stack spacing={0.15}>
      <ClassificationLine
        label={t("ReportHeader.classification_complainant")}
        value={complainant || t("ReportViewer.no_classification")}
        color={theme.palette.secondary.main}
      />

      {ethicsGlobal && (
        <ClassificationLine
          label={t("ReportHeader.classification_ethicsglobal")}
          value={ethicsGlobal}
          color={theme.palette.secondary.main}
        />
      )}

      {mercadoLibre && (
        <Tooltip
          arrow
          placement="bottom-start"
          TransitionComponent={Zoom}
          TransitionProps={{ timeout: 150 }}
          title={
            <>
              {reclassifiedAt &&
                `${t("ReportHeader.reclassified_on")}: ${parseDateToLgComun4(
                  reclassifiedAt
                )}`}
              {reclassifiedTimes > 1 &&
                ` · ${t("ReportHeader.reclassified_times", {
                  times: reclassifiedTimes,
                })}`}
            </>
          }
        >
          <span>
            <ClassificationLine
              label={t("ReportHeader.classification_mercadolibre")}
              value={mercadoLibre}
              iconName="outline_convert"
              color={theme.palette.pink.main}
            />
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};
